import {
  ArrowPathIcon,
  NewspaperIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { EllipsisVerticalIcon } from '@heroicons/react/20/solid';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';

import PedWorkspaceGuard from '@/components/shared/PedWorkspaceGuard';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Button, Dropdown, Menu } from '@/components/ui/daisy';
import { useConfirm } from '@/data/context/ConfirmModalContext';
import { Breakpoints } from '@/utils/Breakpoint';
import { twMerge } from 'tailwind-merge';

type MoreActionsProps = {
  children: ReactNode;
  trigger?: ReactNode;
};

const MoreActionsRoot = ({ children, trigger }: MoreActionsProps) => {
  const { t } = useTranslation('translation', { keyPrefix: 'common' });
  const isXl = useMediaQuery({ minWidth: Breakpoints.xl });
  return (
    <Dropdown position="bottom" align="end">
      <Dropdown.Trigger>
        <ConditionalRenderer
          condition={trigger}
          fallback={
            <Button
              variant="ghost"
              size={isXl ? 'sm' : 'xs'}
              shape="square"
              aria-label={t('moreOptions')}
            >
              <EllipsisVerticalIcon className="max-xl:size-5 size-6 text-base-content" />
            </Button>
          }
        >
          {trigger}
        </ConditionalRenderer>
      </Dropdown.Trigger>
      <Dropdown.Content>
        <Menu
          className="bg-base-100 shadow-lg rounded-box w-47"
          activeColor="neutral"
        >
          {children}
        </Menu>
      </Dropdown.Content>
    </Dropdown>
  );
};

type DeleteProps = {
  onClick: () => void | Promise<unknown>;
  confirm?: boolean;
  disabled?: boolean;
  text?: string;
};

const Delete = ({ onClick, confirm, disabled = false, text }: DeleteProps) => {
  const { t } = useTranslation('translation', { keyPrefix: 'common' });
  const confirmDelete = useConfirm();

  const handleClick = () => {
    if (confirm) {
      confirmDelete({ onConfirm: onClick });
      return;
    }
    onClick();
  };

  return (
    <PedWorkspaceGuard isBlocked={disabled} placement="left">
      <Menu.Item disabled={disabled}>
        <button
          type="button"
          className={twMerge('flex items-center gap-2 text-error')}
          onClick={handleClick}
          disabled={disabled}
        >
          <TrashIcon className="w-4 h-4" />
          {text ?? t('delete')}
        </button>
      </Menu.Item>
    </PedWorkspaceGuard>
  );
};

type RenameProps = {
  onClick: () => void;
};

const Rename = ({ onClick }: RenameProps) => {
  const { t } = useTranslation('translation', { keyPrefix: 'common' });

  return (
    <Menu.Item>
      <button
        type="button"
        className="flex items-center gap-2"
        onClick={onClick}
      >
        <PencilIcon className="size-4 shrink-0" />
        {t('rename')}
      </button>
    </Menu.Item>
  );
};

type VersioningProps = {
  onClick: () => void | Promise<unknown>;
  text?: string;
};

const Versioning = ({ onClick, text }: VersioningProps) => {
  const { t } = useTranslation('translation', { keyPrefix: 'common' });

  return (
    <Menu.Item>
      <button
        type="button"
        className="flex items-center gap-2"
        onClick={onClick}
      >
        <NewspaperIcon className="size-4 shrink-0" />
        {text ?? t('newVersion')}
      </button>
    </Menu.Item>
  );
};

const Edition = ({ onClick, text }: VersioningProps) => {
  const { t } = useTranslation('translation', { keyPrefix: 'common' });

  return (
    <Menu.Item>
      <button
        type="button"
        className="flex items-center gap-2"
        onClick={onClick}
      >
        <ArrowPathIcon className="size-4 shrink-0" />
        {text ?? t('updateCourse')}
      </button>
    </Menu.Item>
  );
};

MoreActionsRoot.displayName = 'MoreActions';
Delete.displayName = 'MoreActions.Delete';
Rename.displayName = 'MoreActions.Rename';
Versioning.displayName = 'MoreActions.Versioning';
Edition.displayName = 'MoreActions.Edition';
export const MoreActions = Object.assign(MoreActionsRoot, {
  Delete,
  Rename,
  Versioning,
  Edition,
});
